/**
 * Locale-aware formatters. Everything that renders a date, a number,
 * a hash, or a version goes through these so RTL + HE and LTR + EN
 * both get the right shape without per-component branching.
 */

import type { DomainType, Hash256, NormalizedDomain, TrustedTimestamp } from '@/api/types';
import type { Locale } from '@/i18n/config';

/** Shorten a `sha256:abcd…` or `ed25519:abcd…` for display. */
export function truncateHash(hash: string, head = 8, tail = 6): string {
  const colon = hash.indexOf(':');
  if (colon < 0) {
    if (hash.length <= head + tail + 1) return hash;
    return `${hash.slice(0, head)}…${hash.slice(-tail)}`;
  }
  const tag = hash.slice(0, colon + 1);
  const body = hash.slice(colon + 1);
  if (body.length <= head + tail + 1) return hash;
  return `${tag}${body.slice(0, head)}…${body.slice(-tail)}`;
}

/** Relative time (e.g. "2h ago"). */
export function formatRelativeTime(iso: string, locale: Locale): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSeconds = Math.round((then - now) / 1000);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(diffSeconds);
  if (abs < 60) return rtf.format(Math.round(diffSeconds), 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSeconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSeconds / 3600), 'hour');
  if (abs < 2_592_000) return rtf.format(Math.round(diffSeconds / 86400), 'day');
  if (abs < 31_536_000) return rtf.format(Math.round(diffSeconds / 2_592_000), 'month');
  return rtf.format(Math.round(diffSeconds / 31_536_000), 'year');
}

/** Full date + time for tooltips. */
export function formatAbsoluteTime(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

/** TrustedTimestamp → relative string. */
export function relativeFromTimestamp(ts: TrustedTimestamp, locale: Locale): string {
  return formatRelativeTime(ts.iso, locale);
}

/** Grouped-digits number (stats bar). */
export function formatNumber(n: number, locale: Locale): string {
  return new Intl.NumberFormat(locale).format(n);
}

/** Map `DomainType` (tagged enum from Rust) to a normalized lowercase
 *  string the Tailwind classes key off of. `Custom` falls back to
 *  `generic` so the UI never crashes on unknown domains. */
export function normalizeDomain(domain: DomainType): NormalizedDomain {
  if (typeof domain === 'string') {
    switch (domain) {
      case 'Audio': return 'audio';
      case 'Process': return 'process';
      case 'Medical': return 'medical';
      case 'Financial': return 'financial';
      case 'Defense': return 'defense';
      case 'Government': return 'government';
      case 'Generic': return 'generic';
    }
  }
  return 'generic';
}

/** Copy a string to the clipboard, returning whether the browser
 *  accepted the request. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Human-readable label for a Hash256. Used inline next to the
 *  monospace truncation for screen readers. */
export function hashLabel(h: Hash256): string {
  return `${h.slice(0, 7)} ${truncateHash(h, 6, 4)}`;
}
